import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import HomeVisitClinicalRecordForm from "./HomeVisitClinicalRecordForm";
import {
  fetchHomeVisitClinicalRecord,
  updateHomeVisitClinicalRecord,
} from "../../store/slices/homeVisitClinicalRecordSlice";

const EditHomeVisitClinicalRecord = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentRecord } = useSelector((state) => state.homeVisitClinicalRecords);

  const [formData, setFormData] = useState({
    patient_id: patientId || "",
    case_id: caseId || "",

    // Visit Details
    visit_number: "",
    date_of_visit: "",
    time_in: "",
    time_out: "",
    duration: "",
    location_address: "",
    type_of_visit: "",

    // Team
    psychiatrist_name: "",
    rehab_practitioner_name: "",
    rehab_assistant_name: "",
    driver_name: "",
    team_gender: "",
    family_member_name: "",
    family_member_relation: "",
    family_member_contact: "",

    // Safety
    safety_environment_safe: false,
    safety_no_weapons: false,
    safety_no_intoxicated: false,
    safety_client_stable: false,
    safety_family_cooperative: false,
    safety_unsafe_action_description: "",

    // Clinical Status
    clinical_appearance_hygiene: "",
    clinical_behavior: "",
    clinical_speech: "",
    clinical_mood_affect: "",
    clinical_thought_content: "",
    clinical_perception: "",
    clinical_insight: "",
    clinical_judgment: "",
    clinical_suicidal_ideation: false,
    clinical_homicidal_ideation: false,
    clinical_impulsivity: false,
    clinical_risk_level: "",
    clinical_risk_action_taken: "",

    // Medication Review
    med_currently_on: false,
    med_adherence: "",
    med_side_effects: "",
    med_changes: "",
    med_changes_details: "",
    med_prescribed_by: "",

    // Psychoedu
    psychoedu_illness_understanding: false,
    psychoedu_medication_importance: false,
    psychoedu_relapse_signs: false,
    psychoedu_behavior_management: false,
    psychoedu_communication_strategies: false,
    psychoedu_crisis_handling: false,
    psychoedu_rehab_readiness: false,
    psychoedu_residential_vs_drp: false,
    psychoedu_family_response: "",
    psychoedu_notes: "",

    // Engagement
    engagement_participation: "",
    engagement_issues_discussed: "",
    engagement_coping_style: "",
    engagement_motivation: "",

    // Goals
    goals_short_term_1: "",
    goals_short_term_2: "",
    goals_suitability: "",

    // Plan
    plan_decision: "",
    plan_next_visit_date: "",

    // Incident
    incident_aggression_disruption: false,
    incident_description: "",
    incident_action_taken: "",

    // Impression
    impression_summary: "",
    impression_progress: "",

    // Audit
    audit_completed_within_1hour: false,
    audit_documentation_same_day: false,
    audit_medication_chart_updated: false,
    audit_risk_reassessment_completed: false,

    // Signatures
    signature_psychiatrist: "",
    signature_psychiatrist_date: "",
    signature_rehab_practitioner: "",
    signature_family_member: "",

    // Finance fields
    amount: "",
    payment_status: "",
    reference_no: "",
    payment_date: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      dispatch(fetchHomeVisitClinicalRecord(id));
    }
  }, [dispatch, id]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes("T")) {
      return dateStr.split("T")[0];
    }
    return dateStr.split(" ")[0];
  };

  useEffect(() => {
    if (currentRecord) {
      setFormData({
        patient_id: currentRecord.patient_id || patientId || "",
        case_id: currentRecord.case_id || caseId || "",

        visit_number: currentRecord.visit_number || "",
        date_of_visit: formatDateForInput(currentRecord.date_of_visit),
        time_in: currentRecord.time_in || "",
        time_out: currentRecord.time_out || "",
        duration: currentRecord.duration || "",
        location_address: currentRecord.location_address || "",
        type_of_visit: currentRecord.type_of_visit || "",

        psychiatrist_name: currentRecord.psychiatrist_name || "",
        rehab_practitioner_name: currentRecord.rehab_practitioner_name || "",
        rehab_assistant_name: currentRecord.rehab_assistant_name || "",
        driver_name: currentRecord.driver_name || "",
        team_gender: currentRecord.team_gender || "",
        family_member_name: currentRecord.family_member_name || "",
        family_member_relation: currentRecord.family_member_relation || "",
        family_member_contact: currentRecord.family_member_contact || "",

        safety_environment_safe: !!currentRecord.safety_environment_safe,
        safety_no_weapons: !!currentRecord.safety_no_weapons,
        safety_no_intoxicated: !!currentRecord.safety_no_intoxicated,
        safety_client_stable: !!currentRecord.safety_client_stable,
        safety_family_cooperative: !!currentRecord.safety_family_cooperative,
        safety_unsafe_action_description: currentRecord.safety_unsafe_action_description || "",

        clinical_appearance_hygiene: currentRecord.clinical_appearance_hygiene || "",
        clinical_behavior: currentRecord.clinical_behavior || "",
        clinical_speech: currentRecord.clinical_speech || "",
        clinical_mood_affect: currentRecord.clinical_mood_affect || "",
        clinical_thought_content: currentRecord.clinical_thought_content || "",
        clinical_perception: currentRecord.clinical_perception || "",
        clinical_insight: currentRecord.clinical_insight || "",
        clinical_judgment: currentRecord.clinical_judgment || "",
        clinical_suicidal_ideation: !!currentRecord.clinical_suicidal_ideation,
        clinical_homicidal_ideation: !!currentRecord.clinical_homicidal_ideation,
        clinical_impulsivity: !!currentRecord.clinical_impulsivity,
        clinical_risk_level: currentRecord.clinical_risk_level || "",
        clinical_risk_action_taken: currentRecord.clinical_risk_action_taken || "",

        med_currently_on: !!currentRecord.med_currently_on,
        med_adherence: currentRecord.med_adherence || "",
        med_side_effects: currentRecord.med_side_effects || "",
        med_changes: currentRecord.med_changes || "",
        med_changes_details: currentRecord.med_changes_details || "",
        med_prescribed_by: currentRecord.med_prescribed_by || "",

        psychoedu_illness_understanding: !!currentRecord.psychoedu_illness_understanding,
        psychoedu_medication_importance: !!currentRecord.psychoedu_medication_importance,
        psychoedu_relapse_signs: !!currentRecord.psychoedu_relapse_signs,
        psychoedu_behavior_management: !!currentRecord.psychoedu_behavior_management,
        psychoedu_communication_strategies: !!currentRecord.psychoedu_communication_strategies,
        psychoedu_crisis_handling: !!currentRecord.psychoedu_crisis_handling,
        psychoedu_rehab_readiness: !!currentRecord.psychoedu_rehab_readiness,
        psychoedu_residential_vs_drp: !!currentRecord.psychoedu_residential_vs_drp,
        psychoedu_family_response: currentRecord.psychoedu_family_response || "",
        psychoedu_notes: currentRecord.psychoedu_notes || "",

        engagement_participation: currentRecord.engagement_participation || "",
        engagement_issues_discussed: currentRecord.engagement_issues_discussed || "",
        engagement_coping_style: currentRecord.engagement_coping_style || "",
        engagement_motivation: currentRecord.engagement_motivation || "",

        goals_short_term_1: currentRecord.goals_short_term_1 || "",
        goals_short_term_2: currentRecord.goals_short_term_2 || "",
        goals_suitability: currentRecord.goals_suitability || "",

        plan_decision: currentRecord.plan_decision || "",
        plan_next_visit_date: formatDateForInput(currentRecord.plan_next_visit_date),

        incident_aggression_disruption: !!currentRecord.incident_aggression_disruption,
        incident_description: currentRecord.incident_description || "",
        incident_action_taken: currentRecord.incident_action_taken || "",

        impression_summary: currentRecord.impression_summary || "",
        impression_progress: currentRecord.impression_progress || "",

        audit_completed_within_1hour: !!currentRecord.audit_completed_within_1hour,
        audit_documentation_same_day: !!currentRecord.audit_documentation_same_day,
        audit_medication_chart_updated: !!currentRecord.audit_medication_chart_updated,
        audit_risk_reassessment_completed: !!currentRecord.audit_risk_reassessment_completed,

        signature_psychiatrist: currentRecord.signature_psychiatrist || "",
        signature_psychiatrist_date: formatDateForInput(currentRecord.signature_psychiatrist_date),
        signature_rehab_practitioner: currentRecord.signature_rehab_practitioner || "",
        signature_family_member: currentRecord.signature_family_member || "",

        // Finance fields
        amount: currentRecord.amount || "",
        payment_status: currentRecord.payment_status || "",
        reference_no: currentRecord.reference_no || "",
        payment_date: formatDateForInput(currentRecord.payment_date),
      });
    }
  }, [currentRecord, patientId, caseId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.visit_number) newErrors.visit_number = "Visit Number is required";
    if (!formData.date_of_visit) newErrors.date_of_visit = "Date of Visit is required";
    if (!formData.type_of_visit) newErrors.type_of_visit = "Type of Visit is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all required fields in the form (Visit Number, Date, and Type).",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      const payload = {
        ...formData,
        plan_next_visit_date: formData.plan_next_visit_date || null,
        signature_psychiatrist_date: formData.signature_psychiatrist_date || null,
      };

      await dispatch(updateHomeVisitClinicalRecord({ id, formData: payload })).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Home Visit Clinical Record updated successfully.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });

      // Stay on edit page
    } catch (err) {
      let errorMessage = "Failed to update Home Visit Clinical record";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (typeof err === "object" && err !== null) {
        errorMessage = Object.values(err).flat().join("\n");
      }
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        <div className="card shadow-sm border-0">
          <div className="card-header border-bottom py-3 d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">Edit Home Visit Clinical Record Form</h5>
            <div>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
                <i className="bx bx-arrow-left me-1"></i>Back
              </button>
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <HomeVisitClinicalRecordForm
              formData={formData}
              setFormData={setFormData}
              handleChange={handleChange}
              errors={errors}
            />
            <div className="card-footer border-top bg-white py-3 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Update Clinical Record
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditHomeVisitClinicalRecord;
